// DEPENDENCIES
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./DesignJobChangeLog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import { CircularProgress } from "@mui/material";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import TanStackTable from "../../Misc/Table/TanStackTable";

// CONSTANTS
import { convertToPST } from "../../../constants/dateTime";
import {
	dateSort,
	defaultSort,
	notApplicable,
} from "../../Misc/Table/TanStackTableConstants";

// OTHER

// REACT COMPONENT
const DesignJobChangeLog = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATES
	const [designJobChangeLogs, setDesignJobChangeLogs] = useState([]);

	const [designJobChangeLogRows, setDesignJobChangeLogRows] = useState([]);
	const [designJobChangeLogsColumns, setDesignJobChangeLogsColumns] =
		useState([]);

	// Feedback
	const [isLoading, setIsLoading] = useState(false);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		fetchLogs();

		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Design Job Change Logs",
			employeeID: employeeID,
			notes: "Accessed the Design Job Change Logs page",
		});
	}, []);

	useEffect(() => {
		createColumnData();
		createRowData();
	}, [designJobChangeLogs]);

	// INPUT HANDLERS

	// API FUNCTIONS
	const fetchLogs = async () => {
		setIsLoading(true);

		try {
			const response = await getAPICall(
				instance,
				accounts[0],
				"/api/logs/designJobs/all"
			);
			setDesignJobChangeLogs(response.data);
		} catch (error) {
			console.log(error);
		}

		setIsLoading(false);
	};

	// HELPER FUNCTIONS
	const createColumnData = () => {
		setDesignJobChangeLogsColumns([
			{
				accessorKey: "employee_name",
				header: () => <span>Employee</span>,
				label: "Employee",
				cell: ({ row }) => (
					<p>{row.original.employee_name || notApplicable}</p>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "full_project_name",
				header: () => <span>Job</span>,
				label: "Job",
				cell: ({ row }) =>
					row.original.full_project_name ? (
						<a href={`/design/spec_books/${row.original.phase_id}`}>
							{row.original.full_project_name}
						</a>
					) : (
						notApplicable
					),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "description",
				header: () => <span>Description</span>,
				label: "Description",
				cell: ({ row }) => (
					<p>{row.original.description || notApplicable}</p>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "created",
				header: () => <span>Date</span>,
				label: "Date",
				cell: ({ row }) => (
					<p>{row.original.created || notApplicable}</p>
				),
				filterType: "date",
				filterFn: dateSort,
			},
		]);
	};

	const createRowData = () => {
		setDesignJobChangeLogRows(
			designJobChangeLogs.map((log) => ({
				phase_id: log.phase_id,
				employee_name: log.employee_name,
				full_project_name: log.full_project_name,
				description: log.description,
				created: convertToPST(log.created),
			}))
		);
	};

	// RENDER
	return (
		<div className="DesignJobChangeLog">
			<div className="designJobChangeLogHeader">
				<div className="designJobChangeLogHeaderTitle">
					Design Job Logs
				</div>
			</div>
			{isLoading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!isLoading && (
				<div className="tableContainerDiv">
					{designJobChangeLogs.length === 0 && <J2NoResultsFound />}
					{designJobChangeLogs.length > 0 &&
						designJobChangeLogsColumns.length > 0 &&
						designJobChangeLogRows.length > 0 && (
							<TanStackTable
								columnData={designJobChangeLogsColumns}
								rowData={designJobChangeLogRows}
							/>
						)}
				</div>
			)}
		</div>
	);
};

// EXPORT
export default DesignJobChangeLog;
